import { ReactNode, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  BookingBillVm,
  BillRowVm,
} from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import { AddManualLineParams } from '@/features/dashboard/bookingChanges/types/ChangeViewModel.ts';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { Card } from '@/components/ui/Card.tsx';
import MoneyAmountList from '@/features/dashboard/bookingChanges/components/primitives/MoneyAmountList.tsx';
import BillSectionList from './BillSectionList';
import GroupSeparator from './GroupSeparator';
import InstallmentsCard from './InstallmentsCard';
import PaymentsCard from './PaymentsCard';
import PersonTile from './PersonTile';
import TransferTitlePill from './TransferTitlePill';
import { pluralizePeople } from '@/features/dashboard/bookingChanges/utils/plurals.ts';
import { peopleCountLabel } from '@/features/dashboard/bookingChanges/components/bookingDetails/bookingHeaderPresentation.ts';
import {
  BOOKING_WIDE_GROUP_HINT,
  BOOKING_WIDE_GROUP_LABEL,
} from '@/features/dashboard/bookingChanges/utils/changeSummary.ts';

interface BookingBillViewProps extends React.HTMLAttributes<HTMLDivElement> {
  data: BookingBillVm;
  isEditing: boolean;
  onlyChanged: boolean;
  /** Treść rozwiniętego kafla uczestnika — ten sam komponent, co ekran A. */
  renderParticipant: (participantId: string) => ReactNode;
  /** Treść rozwiniętego kafla zapisującego — ten sam komponent, co ekran B. */
  renderClient: () => ReactNode;
  /** Akcja wiersza w sekcjach całej rezerwacji. */
  onRowAction: (row: BillRowVm) => void;
  onAddDiscount: () => void;
  onAddParticipant: () => void;
  /** Sekcje bez przypisania do osoby — widok nie dopina uczestnika. */
  onAddManualRow?: (params: AddManualLineParams) => void;
  /** Osoba, z której ekranu przyszedł operator — jej kafel dostaje ciemniejszą ramkę. */
  currentPersonId?: string;
}

/**
 * Ekran rezerwacji (C1/C2) — poziom całej paczki: akordeon osób, pozycje bez
 * przypisania do osoby i harmonogram płatności.
 *
 * Rozwinięty kafel wpuszcza dokładnie ten sam komponent, co osobny ekran osoby,
 * dlatego treść przychodzi przez `renderParticipant` / `renderClient` zamiast
 * być zaszyta tutaj. Raty i wpłaty stoją tylko na tym ekranie, bo dotyczą całej
 * rezerwacji i nie dzielą się na uczestników.
 */
const BookingBillView = ({
  data,
  isEditing,
  onlyChanged,
  renderParticipant,
  renderClient,
  onRowAction,
  onAddDiscount,
  onAddParticipant,
  onAddManualRow,
  currentPersonId,
  className,
  ...props
}: BookingBillViewProps) => {
  // Podgląd (C1) startuje ze wszystkimi kaflami zwiniętymi — ma być listą osób
  // z kwotami, a nie rozwiniętym rachunkiem jednej z nich. Tryb zmiany (C2)
  // otwiera pierwszego uczestnika, bo tam operator przychodzi po to, żeby coś
  // zmienić.
  const [openPersonId, setOpenPersonId] = useState<string | null>(() =>
    isEditing ? (data.participants[0]?.person.id ?? null) : null,
  );

  // Akordeon obejmuje wszystkie kafle razem — rozwinięcie jednej osoby zwija
  // pozostałe, także zapisującego.
  const togglePerson = (personId: string) =>
    setOpenPersonId((open) => (open === personId ? null : personId));

  return (
    <div className={cn('flex flex-col gap-3.5', className)} {...props}>
      <GroupSeparator label="Zapisujący" hint={pluralizePeople(1)} />

      <PersonTile
        tile={data.client}
        isEditing={isEditing}
        isCurrent={data.client.person.id === currentPersonId}
        isOpen={openPersonId === data.client.person.id}
        onToggle={() => togglePerson(data.client.person.id)}
      >
        {renderClient()}
      </PersonTile>

      <GroupSeparator
        className="pt-3.5"
        label="Uczestnicy"
        hint={peopleCountLabel(data.participants)}
      />

      {data.participants.map((participant) => (
        <PersonTile
          key={participant.person.id}
          tile={participant}
          isEditing={isEditing}
          isCurrent={participant.person.id === currentPersonId}
          isOpen={openPersonId === participant.person.id}
          onToggle={() => togglePerson(participant.person.id)}
        >
          {renderParticipant(participant.person.id)}
        </PersonTile>
      ))}

      {/*
        Tryb zmiany kończy ekran na pozycjach całej rezerwacji: wartość
        rezerwacji i harmonogram płatności należą do stanu obowiązującego, a
        ten w edycji jest nieaktualny — kwotę po zmianie niesie karta „Wpływ
        paczki” w panelu bocznym, a bilans szkicu pasek „Wartość zmian”.
      */}
      {isEditing ? (
        <>
          <GroupSeparator
            className="pt-3.5"
            label={BOOKING_WIDE_GROUP_LABEL}
            hint={BOOKING_WIDE_GROUP_HINT}
          />

          <BillSectionList
            sections={data.bookingWideSections}
            isEditing={isEditing}
            onlyChanged={onlyChanged}
            onRowAction={onRowAction}
            onAddManualRow={(section) =>
              onAddManualRow?.({ sectionId: section.id })
            }
          />

          <div className="flex flex-wrap gap-2 px-1 py-0.5">
            <ActionButton tone="dashed" size="sm" onClick={onAddDiscount}>
              + Zniżka
            </ActionButton>
            <ActionButton tone="dashed" size="sm" onClick={onAddParticipant}>
              + Uczestnik
            </ActionButton>
          </div>
        </>
      ) : (
        <>
          <Card className="flex flex-wrap items-baseline justify-between gap-x-5 gap-y-1 overflow-hidden px-4 py-4 sm:px-7">
            <span className="text-[15px] font-bold text-ink">
              Wartość rezerwacji
            </span>
            <span className="text-2xl font-extrabold tabular-nums text-ink">
              <MoneyAmountList values={data.totalValue} />
            </span>
          </Card>

          <div className="flex flex-col gap-2 pt-3.5">
            <GroupSeparator
              label="Płatności"
              hint="wspólne dla całej rezerwacji"
            />
            <TransferTitlePill clientId={data.clientId} tourId={data.tourId} />
          </div>

          <InstallmentsCard
            installments={data.installments}
            clientId={data.clientId}
          />
          <PaymentsCard
            payments={data.payments}
            bookingId={data.bookingId}
            clientId={data.clientId}
          />
        </>
      )}
    </div>
  );
};

export default BookingBillView;
